/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  AuthOrg,
  AuthSession,
  AuthStatus,
  OAuthStartArgs,
  SignInCredentials,
  UserRole,
} from './types';
import { env } from '../../env';
import { queryClient, resetSessionExpired } from '../api';
import { refreshAccessToken } from '../api/client';
import { publishSession } from './session-snapshot';

/**
 * AuthProvider — interface and context for authentication.
 *
 * Concrete provider that calls conmap-auto backend endpoints:
 *   POST /auth/login          → email/password sign-in
 *   POST /auth/refresh        → token refresh
 *   GET  /auth/userInfo       → hydrate session from stored token
 *   GET  /auth/google/start   → OAuth redirect
 *   GET  /auth/github/start   → OAuth redirect
 *   GET  /auth/sso/start      → enterprise SSO (Dex) redirect
 *
 * Token storage is in localStorage (shared with @shared/api/client.ts).
 * The api client auto-attaches Bearer tokens and handles 401 refresh.
 */
export interface AuthContextValue {
  status: AuthStatus;
  session: AuthSession | null;
  signIn: (credentials: SignInCredentials) => Promise<void>;
  signInWithOAuth: (args: OAuthStartArgs) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

const defaultValue: AuthContextValue = {
  status: 'loading',
  session: null,
  signIn: async () => {},
  signInWithOAuth: async () => {},
  signOut: async () => {},
  refresh: async () => false,
};

export const AuthContext = createContext<AuthContextValue>(defaultValue);

const BACKEND_ROLE_MAP: Record<string, UserRole> = {
  org_admin: 'org_admin',
  iam_admin: 'iam_admin',
  developer: 'developer',
  analyst: 'analyst',
  viewer: 'viewer',
  // Legacy aliases
  owner: 'org_admin',
  admin: 'org_admin',
  member: 'developer',
};

function mapRole(backendRole: string | undefined | null): UserRole {
  if (!backendRole) return 'developer';
  return BACKEND_ROLE_MAP[backendRole] ?? 'developer';
}

interface BackendUserInfo {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  platformDefaultOrgId: string | null;
  organizationMemberships?: Array<{
    orgId: string;
    role: string;
    organization: {
      orgId: string;
      name: string;
      domain?: string | null;
      slug?: string | null;
      createdAt?: string | null;
      /** Org lifecycle status; a soft-deleted org must not surface anywhere. */
      status?: string | null;
    };
  }>;
}

function toAuthOrg(
  o: NonNullable<BackendUserInfo['organizationMemberships']>[number]['organization']
): AuthOrg {
  return {
    id: o.orgId,
    name: o.name,
    slug: o.slug || o.domain || o.orgId,
    ...(o.createdAt ? { createdAt: o.createdAt } : {}),
  };
}

// Exported for unit tests (the MSW+jsdom provider test can't run on Node 25's
// native-localStorage builds); this is the pure session-shaping logic.
export function buildSession(user: BackendUserInfo): AuthSession {
  // Never surface a soft-deleted org in the session — it feeds the org switcher,
  // the active-org resolution, and the role map (#392). The backend also filters
  // these out of userInfo (armoriq/conmap-auto#669), but doing it here too keeps
  // a stale/undeployed backend from leaking a deleted org into the switcher.
  const memberships = (user.organizationMemberships ?? []).filter(
    (m) => m.organization?.status !== 'deleted'
  );
  const membership = memberships[0];
  const org = membership?.organization;
  const role = mapRole(membership?.role);
  // Per-org role map so the effective role can resolve against the ACTIVELY-
  // selected org, not just memberships[0] (#318). Keyed by the same org id the
  // orgs list / useCurrentOrgId use (organization.orgId), so lookups line up.
  const roleByOrgId: Record<string, UserRole> = {};
  for (const m of memberships) {
    const id = m.organization?.orgId ?? m.orgId;
    if (id) roleByOrgId[id] = mapRole(m.role);
  }
  const name =
    user.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email.split('@')[0];

  return {
    user: {
      id: user.userId,
      email: user.email,
      name,
      avatarUrl: user.avatarUrl ?? null,
      role,
    },
    org: org ? toAuthOrg(org) : null,
    orgs: memberships.map((m) => toAuthOrg(m.organization)),
    role,
    roleByOrgId,
    platformDefaultOrgId: user.platformDefaultOrgId,
  };
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const base = env.BACKEND_URL.replace(/\/$/, '');
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as Record<string, unknown>)?.message ?? res.statusText;
    throw new Error(String(msg));
  }
  return res.json() as Promise<T>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Non-React readers (apiClient's default org header) resolve against the
// session React has committed. A layout effect publishes it before any passive
// effect, so no query fetch runs against a stale copy.
function usePublishedSession(session: AuthSession | null): void {
  useLayoutEffect(() => {
    publishSession(session);
    return () => publishSession(null);
  }, [session]);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<AuthSession | null>(null);
  usePublishedSession(session);

  // Mirror the live session into a ref so the cross-tab `storage` listener
  // (registered once) reads the current account without a stale closure.
  const sessionRef = useRef<AuthSession | null>(session);
  sessionRef.current = session;

  // The access token THIS tab is actually authenticated with. `session` does not
  // carry it, and the cross-tab listener below needs it to tell "the user signed
  // out" apart from "another tab cleaned up its own stale token" (#481).
  const tokenRef = useRef<string | null>(null);

  const hydrateSession = useCallback(async (token: string) => {
    try {
      const res = await fetchJson<{ success: boolean; data: BackendUserInfo }>('/auth/userInfo', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.success && res.data) {
        // Every auth path funnels through here, so this is the one place that
        // knows which token actually works for THIS tab (#481).
        tokenRef.current = token;
        setSession(buildSession(res.data));
        setStatus('authenticated');
        return true;
      }
    } catch {
      // Token invalid or expired — fall through
    }
    return false;
  }, []);

  // On mount: try to hydrate from stored token
  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      setStatus('unauthenticated');
      return;
    }

    hydrateSession(token).then((ok) => {
      if (!ok) {
        // Try refresh
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          setStatus('unauthenticated');
          return;
        }
        // Shared single-flight, as above -- two refreshes racing revokes the
        // session that the winner just minted.
        refreshAccessToken()
          .then((accessToken) => {
            if (!accessToken) throw new Error('refresh failed');
            return hydrateSession(accessToken);
          })
          .then((ok2) => {
            if (!ok2) {
              localStorage.removeItem(ACCESS_TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              setStatus('unauthenticated');
            }
          })
          .catch(() => {
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setStatus('unauthenticated');
          });
      }
    });
  }, [hydrateSession]);

  // Cross-tab session isolation (#473). The token lives in shared localStorage,
  // so signing into a different account in another tab swaps this tab's token
  // out silently: the UI keeps one identity while the API serves the other
  // account's data (org list, etc.). Enforce one session per browser,
  // last-login-wins. On a cross-tab token change: sign out if it was removed,
  // re-hydrate for the same account (routine refresh), or wipe caches + reload
  // for a different account so nothing bleeds across.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_KEY) return;
      const nextToken = e.newValue;

      if (!nextToken) {
        // #481: a REMOVED token is not proof the user signed out.
        //
        // The mount path clears these keys whenever it finds a token it cannot
        // use — no refresh token, or a refresh that failed. That is ordinary
        // cleanup for the tab doing it, but it is a `storage` event to every
        // other tab, and treating it as a sign-out logged out sessions that were
        // perfectly alive. Opening a second tab with a stale token was enough to
        // sign the user out of the tab they were working in, and with a
        // 15-minute access token that is routine, not exotic.
        //
        // So: verify before tearing down. If the token this tab authenticated
        // with still works, this tab is the authoritative session — put it back
        // into shared storage (the other tab removed it) and carry on. Only sign
        // out when this tab's own token is dead too, which is the case a real
        // sign-out produces.
        void (async () => {
          const mine = tokenRef.current;
          if (mine && (await hydrateSession(mine))) {
            localStorage.setItem(ACCESS_TOKEN_KEY, mine);
            return;
          }
          tokenRef.current = null;
          setSession(null);
          setStatus('unauthenticated');
          queryClient.clear();
        })();
        return;
      }

      void (async () => {
        try {
          const res = await fetchJson<{ success: boolean; data: BackendUserInfo }>(
            '/auth/userInfo',
            { headers: { Authorization: `Bearer ${nextToken}` } }
          );
          if (!res.success || !res.data) return;
          const next = buildSession(res.data);
          const switchedAccount = sessionRef.current?.user?.id !== next.user?.id;
          if (switchedAccount) {
            queryClient.clear();
            window.location.reload();
            return;
          }
          setSession(next);
          setStatus('authenticated');
        } catch {
          // Token rejected — leave the current session; auth:expired covers it.
        }
      })();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [hydrateSession]);

  // Listen for auth:expired from apiClient
  useEffect(() => {
    const onExpired = () => {
      setSession(null);
      setStatus('unauthenticated');
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const signIn = useCallback(
    async (credentials: SignInCredentials) => {
      const res = await fetchJson<{
        data: { accessToken: string; refreshToken: string };
      }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
      localStorage.setItem(ACCESS_TOKEN_KEY, res.data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, res.data.refreshToken);
      // Re-arm the apiClient's one-shot expiry guard so a future session
      // expiry can redirect again after this fresh login (#61).
      resetSessionExpired();
      await hydrateSession(res.data.accessToken);
    },
    [hydrateSession]
  );

  const signInWithOAuth = useCallback(async (args: OAuthStartArgs) => {
    const base = env.BACKEND_URL.replace(/\/$/, '');
    const redirectTo = encodeURIComponent(
      args.redirectTo || window.location.origin + '/auth/callback'
    );
    let url = `${base}/auth/${args.provider}/start?redirect=${redirectTo}`;
    // Forward an explicit returnTo so the backend preserves the full path +
    // query (it otherwise derives returnTo from the redirect pathname only,
    // which drops the CLI device flow's ?code=/?callback= params).
    if (args.returnTo) {
      url += `&returnTo=${encodeURIComponent(args.returnTo)}`;
    }
    if (args.selectAccount) {
      url += '&prompt=select_account';
    }
    // SSO discovery hands back the org's Dex connector id; forward it so the
    // backend routes straight to that connector instead of showing the picker.
    if (args.connectorId) {
      url += `&connectorId=${encodeURIComponent(args.connectorId)}`;
    }
    window.location.href = url;
  }, []);

  const signOut = useCallback(async () => {
    // Explicit sign-out: drop the remembered token first, so the storage
    // listener's #481 recovery cannot resurrect this session in another tab.
    tokenRef.current = null;
    // Best-effort backend logout (fire-and-forget). The backend endpoint is
    // stateless today (JWT, no token blacklist) but calling it ensures we are
    // ready when server-side revocation is implemented.
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      fetchJson('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        // Swallow errors -- local cleanup below is sufficient.
      });
    }

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // End the session cleanly. Logout previously dropped only the tokens and
    // left `armoriq:current-org:v1` behind, so a later login — even a different
    // user on this same browser — could inherit the prior tenant. The org store
    // clears itself on this event (an event, not a direct call, avoids an
    // import cycle: use-current-org already depends on this module).
    window.dispatchEvent(new Event('auth:signout'));
    // Legacy v1 keys the current app never reads; clear them on logout so a
    // stale org/role blob can't linger and be mistaken for live session state.
    localStorage.removeItem('session');
    localStorage.removeItem('userRole');
    // Defense-in-depth: the dev-role override is no longer read anywhere (#319),
    // but proactively clear any value a previous build persisted so it can never
    // resurface and silently downgrade a real role.
    localStorage.removeItem('armoriq:dev-role:v1');
    // Identity-bearing keys written by the invite-accept flow (#320). Both carry
    // who-you-are, not device prefs: `current-email` is the last-authenticated
    // user's email; `org-memberships:v1` holds [{orgName, role}]. Left behind,
    // they leak the prior identity to the next user on a shared browser.
    localStorage.removeItem('armoriq:current-email');
    localStorage.removeItem('armoriq:org-memberships:v1');
    // Purge the in-memory TanStack Query cache (gcTime 5min). SPA logout→login
    // uses navigate() (no reload), so without this the prior session's
    // role-bearing responses survive the boundary and are served to the next
    // login before they refetch (#320). Uses the shared singleton from
    // @shared/api — never a fresh QueryClient (splitting the cache is a hard
    // rule). No import cycle: query-client only depends on react-query + errors.
    queryClient.clear();
    setSession(null);
    setStatus('unauthenticated');
  }, []);

  const refresh = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (accessToken) {
        return hydrateSession(accessToken);
      }
      setSession(null);
      setStatus('unauthenticated');
      return false;
    }
    try {
      // Shared single-flight: this provider and apiClient must never rotate the
      // refresh token independently, or one revokes the other's session.
      const accessToken = await refreshAccessToken();
      if (!accessToken) throw new Error('refresh failed');
      // Must RETURN it. Falling through resolved `undefined`, so every caller
      // testing `await refresh()` read a successful refresh as a failure. The
      // repo's `tsc --noEmit` never saw it -- the solution-style tsconfig checks
      // zero files, so this needs `-p tsconfig.app.json`.
      return await hydrateSession(accessToken);
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setSession(null);
      setStatus('unauthenticated');
      return false;
    }
  }, [hydrateSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, session, signIn, signInWithOAuth, signOut, refresh }),
    [status, session, signIn, signInWithOAuth, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * StubAuthProvider — kept for reference / test harness. Use AuthProvider
 * for all real usage. Exported so test code can still import it.
 */
const MOCK_SESSION: AuthSession = {
  user: {
    id: 'user_aniket',
    email: 'aniket@armoriq.io',
    name: 'Aniket Sharma',
    avatarUrl: null,
    role: 'org_admin',
  },
  org: { id: 'org_armoriq', name: 'ArmorIQ', slug: 'armoriq' },
  orgs: [{ id: 'org_armoriq', name: 'ArmorIQ', slug: 'armoriq' }],
  role: 'org_admin',
  roleByOrgId: { org_armoriq: 'org_admin' },
  platformDefaultOrgId: 'org_armoriq',
};

export function StubAuthProvider({ children }: { children: ReactNode }) {
  usePublishedSession(MOCK_SESSION);
  const value = useMemo<AuthContextValue>(
    () => ({
      status: 'authenticated' as AuthStatus,
      session: MOCK_SESSION,
      signIn: async () => {},
      signInWithOAuth: async () => {},
      signOut: async () => {},
      refresh: async () => true,
    }),
    []
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
